import {
  collection,
  deleteDoc,
  doc,
  documentId,
  getDoc,
  getDocs,
  orderBy,
  query,
  runTransaction,
  updateDoc,
  where,
} from 'firebase/firestore';
import {
  createInvitationCloudFunction,
  createInvitationsCloudFunction,
  createWorkspaceCloudFunction,
  db,
  deletePermissionsCloudFunction,
  deleteWorkspaceCloudFunction,
} from '@/firebase/init';
import { isUserRole } from '@utils/typeGuards';
import { differenceBy } from 'lodash';
import { Workspace, WorkspaceFromStore } from '@/types/workspaces';
import { useOrganizationStore } from '@/stores/organizationStore';
import { InvitationFromStore, PermissionFromStore, UserRole } from '@/types/types';
import {
  getWorkspaceInvitationsPath,
  getWorkspacePermissionsPath,
} from '@/utils/queryPaths';
import { UserService } from './userService';

export class WorkspacesService {
  static async getWorkspacesByOrganization(
    userId: string,
  ): Promise<WorkspaceFromStore[]> {
    const [, workspacesIds] = await UserService.getEntitiesIds(userId);
    const workspaces: WorkspaceFromStore[] = [];
    const selectedOrganization = useOrganizationStore.getState().selectedOrganization;
    if (!selectedOrganization || workspacesIds.length === 0) return [];
    const snapshot = await getDocs(
      query(
        collection(db, 'workspaces'),
        where(documentId(), 'in', workspacesIds),
        where('organizationId', '==', selectedOrganization.id),
        orderBy('name', 'asc'),
      ),
    );
    if (!snapshot) return [];
    snapshot.forEach((workspace) =>
      workspaces.push({ ...(workspace.data() as Workspace), id: workspace.id }),
    );
    return workspaces;
  }

  static async getAllWorkspaces(userId: string) {
    const [, workspacesIds] = await UserService.getEntitiesIds(userId);
    const workspaces: WorkspaceFromStore[] = [];
    if (workspacesIds.length === 0) return [];
    const snapshot = await getDocs(
      query(
        collection(db, 'workspaces'),
        where(documentId(), 'in', workspacesIds),
        orderBy('name', 'asc'),
      ),
    );
    if (!snapshot) return [];
    snapshot.forEach((workspace) =>
      workspaces.push({ ...(workspace.data() as Workspace), id: workspace.id }),
    );
    return workspaces;
  }

  static async createWorkspace(workspaceName: string, organizationId: string) {
    return await createWorkspaceCloudFunction({ workspaceName, organizationId });
  }

  static async getPermissions(workspaceId: string) {
    const permissions: PermissionFromStore[] = [];
    const snapshot = await getDocs(
      query(
        collection(db, getWorkspacePermissionsPath(workspaceId)),
        orderBy('createdAt', 'asc'),
      ),
    );
    snapshot.forEach((permission) => {
      permissions.push({
        ...permission.data(),
        id: permission.id,
      } as PermissionFromStore);
    });
    return permissions;
  }

  static async getUserPermissions(workspaceId: string, userId: string) {
    const snapshot = await getDoc(
      doc(db, `workspaces/${workspaceId}/permissions/${userId}`),
    );
    if (!snapshot.exists()) return null;
    const permission = { id: snapshot.id, ...snapshot.data() } as PermissionFromStore;
    return permission;
  }

  static async getInvitations(workspaceId: string) {
    const invitations: InvitationFromStore<'workspace'>[] = [];
    const snapshot = await getDocs(
      query(
        collection(db, getWorkspaceInvitationsPath(workspaceId)),
        orderBy('createdAt', 'asc'),
      ),
    );
    snapshot.forEach((invitation) => {
      invitations.push({
        ...invitation.data(),
        id: invitation.id,
      } as InvitationFromStore<'workspace'>);
    });
    return invitations;
  }

  static async changeInvitationRole(
    workspaceId: string,
    invitationId: string,
    role: string,
  ) {
    await updateDoc(doc(db, `workspaces/${workspaceId}/invitations/${invitationId}`), {
      role,
    });
    return null;
  }

  static async changeRole(workspaceId: string, permissionId: string, role: string) {
    await updateDoc(doc(db, `workspaces/${workspaceId}/permissions/${permissionId}`), {
      role,
    });
    return null;
  }

  static async createInvitation(email: string, workspaceId: string) {
    await createInvitationCloudFunction({
      email,
      entityId: workspaceId,
      entityType: 'workspaces',
      role: 'viewer',
    });
    return null;
  }

  static async createInvitations(emails: string[], workspaceId: string, role: UserRole) {
    await createInvitationsCloudFunction({
      emails,
      entityId: workspaceId,
      entityType: 'workspaces',
      role,
    });
    return null;
  }

  static async deleteInvitation(hash: string, workspaceId: string) {
    const invitationsSnap = await getDocs(
      query(
        collection(db, `workspaces/${workspaceId}/invitations`),
        where('hash', '==', hash),
      ),
    );
    const invitation = invitationsSnap.docs[0];
    if (invitation) {
      await deleteDoc(invitation.ref);
    }
    return null;
  }

  static async deletePermission(permissionId: string, workspaceId: string) {
    await deletePermissionsCloudFunction({ workspaceId, usersIds: [permissionId] });
    return null;
  }

  static async deleteWorkspace(
    workspaceId: string,
    organizationId: string,
    region: string,
  ) {
    await deleteWorkspaceCloudFunction({
      workspaceId,
      organizationId,
      region,
    });
    return null;
  }

  static async updateWorkspaceName(workspaceId: string, workspaceName: string) {
    await updateDoc(doc(db, `workspaces/${workspaceId}`), {
      name: workspaceName,
    });
    return null;
  }

  static async deleteSelectedUsers(
    workspaceId: string,
    selected: { id: string; role: UserRole | string; hash?: string }[],
  ) {
    const selectedPermissions = selected.filter((item) => isUserRole(item.role));
    const selectedInvitations = differenceBy(selected, selectedPermissions, 'id');

    const fetchedInvitations = await Promise.all(
      selectedInvitations.map(async (inv) => {
        const snap = await getDocs(
          query(
            collection(db, `workspaces/${workspaceId}/invitations`),
            where('hash', '==', inv.hash),
          ),
        );
        return snap.docs[0];
      }),
    );

    const permissionsIds = selectedPermissions.map((doc) => doc.id);

    if (selectedPermissions.length > 0) {
      await deletePermissionsCloudFunction({ usersIds: permissionsIds, workspaceId });
    }
    await runTransaction(db, async (transaction) => {
      selectedPermissions.forEach((permission) => {
        transaction.delete(
          doc(db, `workspaces/${workspaceId}/permissions/${permission.id}`),
        );
      });

      fetchedInvitations.forEach((invitation) => {
        transaction.delete(invitation.ref);
      });
    });

    return null;
  }
}
